import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { trackSearchPerformed } from "@/lib/firebase";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { UrgencyBadge } from "@/components/shared/UrgencyBadge";
import { EaseStars } from "@/components/shared/EaseStars";
import SmartOfferCard from "@/components/cards/SmartOfferCard";
import { OfferCard } from "@/components/shared/OfferCard";
import { BottomNav, UnifiedFilterSheet } from "@/components/shared/Navigation";
import MultiDimensionalFilterSheet, { ActiveFilterChips, loadFiltersFromSession, clearFilterSession } from "@/components/filters/MultiDimensionalFilterSheet";
import { ShareSheet, getShareCopy } from "@/components/shared/ShareSheet";
import {
  Search, Gift, Star, Clock, MapPin, Loader2, ChevronRight,
  ChevronDown, Trophy, Send, Sparkles, Cloud, PartyPopper,
  Compass, ListChecks, BookOpen, Flame, Bookmark, X,
} from "lucide-react";
import BirthdayEscalationCard from "@/components/shared/BirthdayEscalationCard";
import FoodTruckDiscoverSection from "@/components/discover/FoodTruckDiscoverSection";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import WorthAnotherLook from "@/components/shared/WorthAnotherLook";
import HomeTopBar from "@/components/home/HomeTopBar";
import ModeSwitcher from "@/components/home/ModeSwitcher";
import BirthdayGreeting from "@/components/home/BirthdayGreeting";
import BirthdayCTA from "@/components/home/BirthdayCTA";
import DiscoverCountdown from "@/components/home/DiscoverCountdown";
import ConciergeCard from "@/components/home/ConciergeCard";
import NewThisWeek from "@/components/discover/NewThisWeek";
import TrendingInCity from "@/components/discover/TrendingInCity";
import NearMeNow from "@/components/discover/NearMeNow";
import PowerDealsSection from "@/components/discover/PowerDealsSection";
import DealCalendar from "@/components/discover/DealCalendar";
import MonthlyChallengeBanner from "@/components/discover/MonthlyChallengeBanner";
import PerfectMonthTracker from "@/components/shared/PerfectMonthTracker";
import FriendsHome from "@/components/friends/FriendsHome";
import CityProgressRing from "@/components/discover/CityProgressRing";
import BirthdaySeasonCountdown from "@/components/discover/BirthdaySeasonCountdown";
import CommunityPicks from "@/components/discover/CommunityPicks";
import NotificationSoftAsk from "@/components/shared/NotificationSoftAsk";
import { getNotificationPermission } from "@/lib/fcm";
import { trackPageView, trackSort, trackTabSwitch, trackError, trackScrollDepth, trackDealsViewed } from "@/lib/track";
import {
  trackOfferSaved,
  trackOfferUnsaved,
  trackSearchPerformed as trackPhSearchPerformed,
  trackFilterApplied,
  trackBirthdayCountdownTapped,
  trackGreetingDisplayed,
  trackPostBirthdaySurveyStarted,
  trackWantedPromoAdded,
  trackWhatsNewItemViewed,
} from "@/lib/events";
import TooltipOverlay from "@/components/shared/TooltipOverlay";
import LiveActivityFeed from "@/components/discover/LiveActivityFeed";
import TipOfTheDay from "@/components/shared/TipOfTheDay";
import IpLocationBanner from "@/components/shared/IpLocationBanner";
import { useUserState } from "@/contexts/UserStateContext";
import PostTripSummaryCard from "@/components/PostTripSummaryCard";
import HomeContributeCards from "@/components/home/HomeContributeCards";

export default function HomePage() {
  const { user, loading: authLoading } = useAuth();
  const { mode, theme } = useTheme();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [bestValue, setBestValue] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [acResults, setAcResults] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const searchRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const cat = searchParams.get("category");
    return cat ? [cat] : [];
  });
  const [sortBy, setSortBy] = useState("urgency");
  const [favorites, setFavorites] = useState([]);
  const [halalFilter, setHalalFilter] = useState(false);
  const [halalCertifiedFilter, setHalalCertifiedFilter] = useState(false);
  const [veganFilter, setVeganFilter] = useState(false);
  const [vegetarianFilter, setVegetarianFilter] = useState(false);
  const [glutenFreeFilter, setGlutenFreeFilter] = useState(false);
  const [boycottFilter, setBoycottFilter] = useState(false);
  const hasDietaryFilter = halalFilter || halalCertifiedFilter || veganFilter || vegetarianFilter || glutenFreeFilter;
  const isDiscover = mode === "discover";
  const [noAppFilter, setNoAppFilter] = useState(false);
  const [highValueFilter, setHighValueFilter] = useState(false);
  const [easyClaimFilter, setEasyClaimFilter] = useState(false);
  const [verifiedFilter, setVerifiedFilter] = useState(false);
  const [trueFreebieFilter, setTrueFreebieFilter] = useState(false);
  const [windowFilter, setWindowFilter] = useState("");
  const [bogoFilter, setBogoFilter] = useState(false);
  const [rewardTypeFilter, setRewardTypeFilter] = useState("");
  const [inStoreFilter, setInStoreFilter] = useState(false);
  const [kidsFilter, setKidsFilter] = useState(false);
  const [offersOpen, setOffersOpen] = useState(() => !!searchParams.get("category"));
  const [topPicks, setTopPicks] = useState([]);
  const [topPicksOpen, setTopPicksOpen] = useState(false);
  const [activePickTab, setActivePickTab] = useState(0);
  const [shareOffer, setShareOffer] = useState(null);
  const [hasItinerary, setHasItinerary] = useState(false);
  const [readinessScore, setReadinessScore] = useState(null);
  const [collectionTempo, setCollectionTempo] = useState(null);
  const [itineraryStats, setItineraryStats] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [showWishlistTooltip, setShowWishlistTooltip] = useState(false);
  const [whatsNewTeaser, setWhatsNewTeaser] = useState(null);
  const [showNotifAsk, setShowNotifAsk] = useState(false);
  // v5.3.1: Multi-dimensional filter state
  const [mdFilters, setMdFilters] = useState(() => loadFiltersFromSession() || {
    categories: [], groupProfile: [], seasonalTags: [], vibe: "",
    priceTier: "", logisticsFlags: [], offerQuality: "",
    verifiedOnly: false, newOnly: false, openNow: false,
  });
  const [showMdFilter, setShowMdFilter] = useState(false);

  const fetchData = useCallback(async () => {
    // Discover mode requires at least one dietary filter
    if (isDiscover && !hasDietaryFilter) {
      setOffers([]);
      setLoading(false);
      return;
    }
    try {
      let url = `${API}/offers?mode=${mode}&sort=${isDiscover ? "name" : sortBy}`;
      if (!isDiscover && selectedCategory.length > 0) url += `&category=${encodeURIComponent(selectedCategory.join(","))}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (halalCertifiedFilter) url += `&halal_certified=yes`;
      else if (halalFilter) url += `&halal=yes`;
      if (veganFilter) url += `&vegan=yes`;
      if (vegetarianFilter) url += `&vegetarian=yes`;
      if (glutenFreeFilter) url += `&gluten_free=yes`;
      if (boycottFilter) url += `&boycott_filter=exclude`;
      if (!isDiscover) {
        if (noAppFilter) url += `&no_app=yes`;
        if (highValueFilter) url += `&high_value=yes`;
        if (easyClaimFilter) url += `&easy_claim=yes`;
        if (verifiedFilter) url += `&verified=yes`;
        if (trueFreebieFilter) url += `&true_freebie=yes`;
        if (windowFilter) url += `&window_days=${windowFilter}`;
        if (bogoFilter) url += `&bogo=yes`;
        if (rewardTypeFilter) url += `&reward_type=${rewardTypeFilter}`;
        if (inStoreFilter) url += `&in_store_only=yes`;
        if (kidsFilter) url += `&kids_friendly=yes`;
      }
      // v5.3.1: Multi-dimensional filter params
      if (mdFilters.categories?.length > 0) url += `&categories=${encodeURIComponent(mdFilters.categories.join(","))}`;
      if (mdFilters.groupProfile?.length > 0) url += `&group_profile=${encodeURIComponent(mdFilters.groupProfile.join(","))}`;
      if (mdFilters.seasonalTags?.length > 0) url += `&seasonal_tags=${encodeURIComponent(mdFilters.seasonalTags.join(","))}`;
      if (mdFilters.vibe) url += `&vibe_filter=${encodeURIComponent(mdFilters.vibe)}`;
      if (mdFilters.priceTier) url += `&price_tier=${encodeURIComponent(mdFilters.priceTier)}`;
      if (mdFilters.logisticsFlags?.length > 0) url += `&logistics_flags=${encodeURIComponent(mdFilters.logisticsFlags.join(","))}`;
      if (mdFilters.offerQuality) url += `&offer_quality=${encodeURIComponent(mdFilters.offerQuality)}`;
      if (mdFilters.verifiedOnly) url += `&verified_only=yes`;
      if (mdFilters.newOnly) url += `&new_only=yes`;
      if (mdFilters.openNow) url += `&open_now=yes`;

      const requests = [
        axios.get(url),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/favorites`),
        axios.get(`${API}/wishlist`).catch(() => ({ data: { items: [], count: 0 } })),
      ];
      if (!isDiscover) {
        requests.push(
          axios.get(`${API}/offers/expiring-soon`),
          axios.get(`${API}/offers/best-value`),
          axios.get(`${API}/offers/top-picks`),
        );
      }

      const results = await Promise.all(requests);
      setOffers(results[0].data);
      setCategories(results[1].data.categories);
      setStats(results[2].data);
      setFavorites(results[3].data.map(f => f.business_id));
      setWishlistIds(results[4].data.items.map(i => i.business_id));
      setWishlistCount(results[4].data.count);
      if (!isDiscover) {
        setExpiringSoon(results[5]?.data || []);
        setBestValue(results[6]?.data || []);
        setTopPicks(results[7]?.data || []);
      }
      // Fetch What's New teaser
      axios.get(`${API}/whats-new`).then(r => {
        if (r.data.items?.length > 0) setWhatsNewTeaser(r.data.items[0]);
      }).catch(() => {});
    } catch (e) {
      console.error("Fetch error:", e);
      trackError("home_fetch", e);
    } finally {
      setLoading(false);
    }
  }, [mode, isDiscover, sortBy, selectedCategory, search, halalFilter, halalCertifiedFilter, veganFilter, vegetarianFilter, glutenFreeFilter, boycottFilter, noAppFilter, highValueFilter, easyClaimFilter, verifiedFilter, trueFreebieFilter, windowFilter, bogoFilter, rewardTypeFilter, inStoreFilter, kidsFilter, hasDietaryFilter, mdFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { trackPageView("home"); }, []);

  // Auto daily streak check-in
  useEffect(() => {
    axios.post(`${API}/streak/checkin`).catch(() => {});
  }, []);

  // Show notification soft ask after 1st claim
  useEffect(() => {
    if (getNotificationPermission() !== "default") return;
    axios.get(`${API}/notifications/prefs`).then(({ data }) => {
      if (data.has_token || data.ask_deferred) return;
      // Check if user has any claimed deals
      axios.get(`${API}/tracker`).then(({ data: t }) => {
        if (t?.items?.some(i => i.claimed)) setShowNotifAsk(true);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  // Load saved dietary preferences on mount
  const dietaryDefaultsLoaded = useRef(false);
  const [savedDietaryDefaults, setSavedDietaryDefaults] = useState({ halal: false, vegan: false, vegetarian: false, gluten_free: false });
  useEffect(() => {
    if (dietaryDefaultsLoaded.current) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/dietary-preferences`);
        const prefs = res.data || {};
        setSavedDietaryDefaults(prefs);
        if (prefs.halal) setHalalFilter(true);
        if (prefs.vegan) setVeganFilter(true);
        if (prefs.vegetarian) setVegetarianFilter(true);
        if (prefs.gluten_free) setGlutenFreeFilter(true);
        dietaryDefaultsLoaded.current = true;
      } catch (e) { dietaryDefaultsLoaded.current = true; }
    })();
  }, []);

  const saveDietaryDefaults = useCallback(async () => {
    const prefs = { halal: halalFilter || halalCertifiedFilter, vegan: veganFilter, vegetarian: vegetarianFilter, gluten_free: glutenFreeFilter };
    try {
      const res = await axios.put(`${API}/auth/dietary-preferences`, prefs);
      setSavedDietaryDefaults(res.data.dietary_preferences);
      return true;
    } catch (e) { return false; }
  }, [halalFilter, halalCertifiedFilter, veganFilter, vegetarianFilter, glutenFreeFilter]);

  const dietaryDiffersFromDefaults = (halalFilter || halalCertifiedFilter) !== savedDietaryDefaults.halal
    || veganFilter !== savedDietaryDefaults.vegan
    || vegetarianFilter !== savedDietaryDefaults.vegetarian
    || glutenFreeFilter !== savedDietaryDefaults.gluten_free;

  // Check itinerary
  useEffect(() => {
    axios.get(`${API}/itinerary/me`).then(res => {
      if (res.data?.stops?.length) {
        setHasItinerary(true);
        if (res.data.collection_tempo) setCollectionTempo(res.data.collection_tempo);
        // Calculate readiness from prep actions
        const stops = res.data.stops || [];
        const claimed = stops.filter(s => s.claimed).length;
        const total = stops.length;
        setItineraryStats({ claimed, total, remaining: total - claimed });
        let totalPrep = 0, donePrep = 0;
        stops.forEach(s => {
          const preps = s.prep_actions || [];
          totalPrep += preps.length;
          donePrep += preps.filter(p => p.completed).length;
        });
        if (totalPrep > 0) {
          setReadinessScore(Math.round((donePrep / totalPrep) * 100));
        }
      }
    }).catch(() => {});
  }, []);

  // Scroll depth tracking
  const maxScrollRef = useRef(0);
  const scrollSentRef = useRef(false);
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);
      if (pct > maxScrollRef.current) maxScrollRef.current = pct;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (maxScrollRef.current > 0 && !scrollSentRef.current) {
        scrollSentRef.current = true;
        trackScrollDepth("home", maxScrollRef.current);
      }
    };
  }, []);

  // Deals viewed tracking
  const viewedDealsRef = useRef(new Set());
  useEffect(() => {
    if (!offersOpen) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-business-id");
            if (id) viewedDealsRef.current.add(id);
          }
        });
      },
      { threshold: 0.5 }
    );
    const cards = document.querySelectorAll('[data-testid="all-offers-section"] [data-business-id]');
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [offersOpen, offers]);

  useEffect(() => {
    return () => {
      if (viewedDealsRef.current.size > 0) {
        trackDealsViewed("home", Array.from(viewedDealsRef.current));
      }
    };
  }, []);

  // Search logging
  useEffect(() => {
    if (!search || search.length < 2) return;
    const timer = setTimeout(() => {
      axios.post(`${API}/admin/log-search`, { query: search, results_count: offers.length }).catch(() => {});
      trackSearchPerformed(search);
      // v1.0.0 GATE 3 — PostHog search_performed
      const activeFilters = Object.entries(mdFilters || {})
        .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : !!v))
        .map(([k]) => k);
      trackPhSearchPerformed(search.length, activeFilters, offers.length);
    }, 1500);
    return () => clearTimeout(timer);
  }, [search, offers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Did you mean?" suggestion when search returns no results
  useEffect(() => {
    if (!search || search.length < 2 || offers.length > 0) {
      setSearchSuggestion(null);
      return;
    }
    const timer = setTimeout(() => {
      axios.get(`${API}/offers/search-suggest?q=${encodeURIComponent(search)}`)
        .then(res => setSearchSuggestion(res.data?.suggestion || null))
        .catch(() => setSearchSuggestion(null));
    }, 600);
    return () => clearTimeout(timer);
  }, [search, offers.length]);

  // Autocomplete dropdown
  useEffect(() => {
    if (!search || search.length < 2) {
      setAcResults([]);
      setAcOpen(false);
      return;
    }
    const timer = setTimeout(() => {
      axios.get(`${API}/search/autocomplete?q=${encodeURIComponent(search)}`)
        .then(res => {
          const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
          setAcResults(items);
          setAcOpen(items.length > 0);
        })
        .catch(() => { setAcResults([]); setAcOpen(false); });
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  // Dismiss autocomplete on click outside or Escape
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setAcOpen(false);
    };
    const handleKey = (e) => { if (e.key === "Escape") setAcOpen(false); };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("mousedown", handleClick); document.removeEventListener("keydown", handleKey); };
  }, []);

  // Filter usage logging
  useEffect(() => {
    const activeFilters = {
      ...(halalFilter && { halal: true }),
      ...(halalCertifiedFilter && { halal_certified: true }),
      ...(veganFilter && { vegan: true }),
      ...(vegetarianFilter && { vegetarian: true }),
      ...(boycottFilter && { boycott: true }),
      ...(mode !== "birthday" && { mode }),
      ...(noAppFilter && { no_app: true }),
      ...(highValueFilter && { high_value: true }),
      ...(easyClaimFilter && { easy_claim: true }),
      ...(verifiedFilter && { verified: true }),
      ...(trueFreebieFilter && { true_freebie: true }),
      ...(windowFilter && { window_days: windowFilter }),
      ...(bogoFilter && { bogo: true }),
      ...(rewardTypeFilter && { reward_type: rewardTypeFilter }),
      ...(inStoreFilter && { in_store: true }),
      ...(kidsFilter && { kids: true }),
      ...(selectedCategory.length > 0 && { category: selectedCategory.join(",") }),
    };
    if (Object.keys(activeFilters).length === 0) return;
    const timer = setTimeout(() => {
      axios.post(`${API}/log/filter-usage`, { filters: activeFilters }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [halalFilter, veganFilter, vegetarianFilter, boycottFilter, noAppFilter, highValueFilter, easyClaimFilter, verifiedFilter, trueFreebieFilter, windowFilter, bogoFilter, rewardTypeFilter, inStoreFilter, kidsFilter, selectedCategory]);

  // Auto-scroll to offers on category nav
  useEffect(() => {
    if (!loading && searchParams.get("category")) {
      const timer = setTimeout(() => {
        const el = document.querySelector('[data-testid="all-offers-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading, searchParams]);

  // Sync category filter with URL
  useEffect(() => {
    const urlCat = searchParams.get("category");
    if (urlCat && (!selectedCategory.length || selectedCategory[0] !== urlCat)) {
      setSelectedCategory([urlCat]);
      setOffersOpen(true);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = async (businessId) => {
    try {
      const res = await axios.post(`${API}/favorites/${businessId}`);
      if (res.data.favorited) {
        setFavorites(prev => [...prev, businessId]);
        trackOfferSaved(businessId, null, null);
      } else {
        setFavorites(prev => prev.filter(id => id !== businessId));
        trackOfferUnsaved(businessId);
      }
    } catch (e) {
      console.error("Favorite toggle error:", e);
      trackError("favorite_toggle", e);
    }
  };

  const toggleWishlist = async (businessId) => {
    try {
      const res = await axios.post(`${API}/wishlist/${businessId}`);
      if (res.data.wishlisted) {
        setWishlistIds(prev => [...prev, businessId]);
        setWishlistCount(prev => prev + 1);
        trackWantedPromoAdded(businessId, null, "home");
        // Show first-time tooltip
        if (!localStorage.getItem("wishlist_tooltip_seen")) {
          setShowWishlistTooltip(true);
          localStorage.setItem("wishlist_tooltip_seen", "1");
          setTimeout(() => setShowWishlistTooltip(false), 4000);
        }
      } else {
        setWishlistIds(prev => prev.filter(id => id !== businessId));
        setWishlistCount(prev => Math.max(0, prev - 1));
      }
    } catch (e) {
      console.error("Wishlist toggle error:", e);
    }
  };

  const logDiscovery = (businessId, source) => {
    axios.post(`${API}/log/discovery`, { business_id: businessId, source }).catch(() => {});
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.pageBg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  // ═══ MODE DETERMINATION ═══
  const daysUntil = stats?.days_until_birthday;
  const isBirthdayMode = mode === "birthday";
  const isFriendsMode = mode === "friends";
  const userColor = user?.favorite_color || theme.primary;
  const totalSavings = offers.reduce((acc, o) => acc + (o.offer_value_est || 0), 0);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: theme.pageBg }} data-testid="homepage">
      <IpLocationBanner />
      <TipOfTheDay pageKey="discover_home" />
      {/* ═══ MODE SWITCHER — absolute top ═══ */}
      <ModeSwitcher />

      {/* ═══ TOP BAR ═══ */}
      <HomeTopBar
        userName={user?.first_name}
        daysUntil={daysUntil}
        userColor={userColor}
        isBirthdayMode={isBirthdayMode}
      />

      {/* Search bar moved to position 5 — after Primary Actions */}

      {/* ═══ v5.3.1: Multi-Dimensional Filter Sheet (modal — position independent) ═══ */}
      <MultiDimensionalFilterSheet
        isOpen={showMdFilter}
        onClose={() => setShowMdFilter(false)}
        onApply={(newFilters) => {
          setMdFilters(newFilters);
          // Sync dietary filters to existing state vars for URL builder
          setHalalFilter(!!newFilters.halalFilter);
          setHalalCertifiedFilter(!!newFilters.halalCertifiedFilter);
          setVeganFilter(!!newFilters.veganFilter);
          setVegetarianFilter(!!newFilters.vegetarianFilter);
          setGlutenFreeFilter(!!newFilters.glutenFreeFilter);
          // v1.0.0 GATE 3 — fire filter_applied for each active filter
          Object.entries(newFilters || {}).forEach(([key, val]) => {
            if (Array.isArray(val)) {
              val.forEach((v) => trackFilterApplied(key, v));
            } else if (typeof val === "boolean" && val) {
              trackFilterApplied(key, true);
            } else if (val && typeof val === "string") {
              trackFilterApplied(key, val);
            }
          });
        }}
        initialFilters={mdFilters}
      />

      {/* ═══ v5.3.1: Active Filter Chips ═══ */}
      <ActiveFilterChips
        filters={mdFilters}
        onRemove={(key, value) => {
          setMdFilters(prev => {
            const next = { ...prev };
            if (Array.isArray(next[key])) {
              next[key] = next[key].filter(v => v !== value);
            } else if (typeof next[key] === "boolean") {
              next[key] = false;
            } else {
              next[key] = "";
            }
            sessionStorage.setItem("bdl_filters_v531", JSON.stringify(next));
            return next;
          });
        }}
        onClearAll={() => {
          const empty = {
            categories: [], groupProfile: [], seasonalTags: [], vibe: "",
            priceTier: "", logisticsFlags: [], offerQuality: "",
            verifiedOnly: false, newOnly: false, openNow: false,
          };
          setMdFilters(empty);
          clearFilterSession();
        }}
      />

      {/* ═══ NOTIFICATION SOFT ASK — feature flagged off at v1.0 ═══ */}
      {/* {showNotifAsk && <NotificationSoftAsk onDismiss={() => setShowNotifAsk(false)} />} */}

      {/* ═══ LIVE ACTIVITY FEED — feature flagged off at v1.0 ═══ */}
      {/* {!isFriendsMode && <LiveActivityFeed />} */}

      {/* ═══ POSITION 3: GREETING ENGINE — state-based, copy bank driven ═══ */}
      {/* Post-Trip Summary (Gate 4 Item 3) renders above greeting during post_birthday_7 */}
      <PostTripSummaryCard />
      <GreetingEngine />

      {/* ═══ POSITION 4: PRIMARY ACTIONS — state-based CTAs ═══ */}
      <PrimaryActions />

      {/* ═══ POSITION 5: SEARCH BAR + FILTERS ═══ */}
      {!isFriendsMode && (
      <div className="px-4 pt-2 pb-2" data-testid="floating-search" ref={searchRef}>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100/80 p-3">
          <form onSubmit={(e) => { e.preventDefault(); setAcOpen(false); }} className="relative flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300 w-4 h-4" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                onFocus={() => { if (acResults.length > 0) setAcOpen(true); }}
                className="w-full pl-11 pr-9 py-2.5 bg-slate-50/80 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 transition-all placeholder:text-slate-300 font-medium"
                style={{ "--tw-ring-color": theme.searchRing }}
                placeholder="Search brands, categories, deals..."
                data-testid="search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setAcOpen(false); setAcResults([]); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                  data-testid="search-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex-shrink-0 w-10 h-10 rounded-xl text-white flex items-center justify-center transition-colors shadow-sm"
              style={{ backgroundColor: theme.buttonBg }}
              data-testid="search-submit-btn"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>
          {acOpen && acResults.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2" data-testid="autocomplete-dropdown">
              {acResults.map((r, i) => (
                <button
                  key={r.business_id}
                  onClick={() => { setAcOpen(false); navigate(`/offer/${r.business_id}`); }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                  data-testid={`autocomplete-item-${i}`}
                >
                  <BrandLogo name={r.name} logoUrl={r.logo_url} size="xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{r.category}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2.5">
            <button
              onClick={() => setShowMdFilter(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border-2 transition-all"
              style={{
                borderColor: Object.values(mdFilters).some(v => (Array.isArray(v) ? v.length > 0 : !!v)) ? '#f59e0b' : `${theme.primary}30`,
                backgroundColor: Object.values(mdFilters).some(v => (Array.isArray(v) ? v.length > 0 : !!v)) ? '#fef3c7' : 'transparent',
                color: Object.values(mdFilters).some(v => (Array.isArray(v) ? v.length > 0 : !!v)) ? '#b45309' : theme.primary,
              }}
              data-testid="md-filter-trigger"
            >
              Filters
              {(() => {
                const count = (mdFilters.categories?.length || 0) + (mdFilters.groupProfile?.length || 0) + (mdFilters.seasonalTags?.length || 0) + (mdFilters.vibe ? 1 : 0) + (mdFilters.priceTier ? 1 : 0) + (mdFilters.logisticsFlags?.length || 0) + (mdFilters.verifiedOnly ? 1 : 0) + (mdFilters.newOnly ? 1 : 0) + (mdFilters.openNow ? 1 : 0) + (mdFilters.halalFilter ? 1 : 0) + (mdFilters.veganFilter ? 1 : 0) + (mdFilters.vegetarianFilter ? 1 : 0) + (mdFilters.glutenFreeFilter ? 1 : 0) + (mdFilters.halalCertifiedFilter ? 1 : 0);
                return count > 0 ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[9px] font-bold">{count}</span> : null;
              })()}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* ═══ v1.0.0 NEW USER EMPTY STATE ═══ */}
      <NewUserEmptyState />

      {/* ═══ MODE CONTENT — crossfade between modes ═══ */}
      <AnimatePresence mode="wait">
        {isBirthdayMode ? (
          <motion.div
            key="birthday-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="birthday-mode"
          >
            {/* Birthday Greeting — only for 11-30 days (planning) where Escalation card isn't shown */}
            {daysUntil > 10 && (
              <div className="mt-4">
                <BirthdayGreeting
                  days={daysUntil}
                  userName={user?.first_name}
                  userColor={userColor}
                  hasItinerary={hasItinerary}
                  totalSavings={totalSavings}
                  collectionTempo={collectionTempo}
                  itineraryStats={itineraryStats}
                />
              </div>
            )}

            {/* Escalation Card — handles day 0, 1-10, and post-birthday with built-in CTAs */}
            {(daysUntil <= 10 || daysUntil < 0) && (
              <div className="mt-4">
                <BirthdayEscalationCard
                  days={daysUntil}
                  userName={user?.first_name}
                  userColor={userColor}
                  hasItinerary={hasItinerary}
                />
              </div>
            )}

            {/* Birthday CTA — only for 11-30 days (Escalation card has its own CTAs for 0-10 & post) */}
            {daysUntil > 10 && (
              <BirthdayCTA
                days={daysUntil}
                hasItinerary={hasItinerary}
                userColor={userColor}
                readinessScore={readinessScore}
              />
            )}

            {/* Your Deals — expiring soon */}
            {expiringSoon.length > 0 && (
              <div className="px-4 mb-5">
                <section data-testid="expiring-soon-section">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-red-500" />Your Deals
                    </h2>
                    <span className="text-xs text-slate-400 font-medium">{expiringSoon.length} coming up</span>
                  </div>
                  <div className="overflow-x-auto pb-2 -mx-4 px-4">
                    <div className="flex gap-3">
                      {expiringSoon.slice(0, 12).map((offer) => {
                        const isActive = offer.deal_status === "active";
                        return (
                          <motion.div
                            key={offer.business_id}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => { logDiscovery(offer.business_id, "expiring_soon"); navigate(`/offer/${offer.business_id}`); }}
                            className={`min-w-[280px] max-w-[280px] rounded-2xl p-5 text-white cursor-pointer flex-shrink-0 shadow-xl border border-white/10 ${isActive ? "bg-gradient-to-br from-red-600 to-orange-500" : "bg-gradient-to-br from-slate-800 to-slate-700"}`}
                            data-testid={`expiring-card-${offer.business_id}`}
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <div className="bg-white/15 rounded-xl p-1.5"><BrandLogo name={offer.name} logoUrl={offer.logo_url} size="sm" /></div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-sm leading-tight">{offer.name}</h3>
                                <p className="text-white/70 text-xs truncate mt-0.5">{offer.offer_title}</p>
                              </div>
                              <span className="text-xl font-black">${offer.offer_value_est}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <EaseStars stars={offer.ease_stars} lightMode />
                              <div className={`text-xs font-bold rounded-xl px-3 py-1.5 ${isActive ? "bg-white text-red-600" : "bg-white/15 text-white"}`}>
                                {isActive
                                  ? `Claim now — ${offer.deal_expires_in}d left`
                                  : `${offer.deal_expires_in} days to use`}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* Birthday Mode Concierge Cards */}
            <div className="px-4 mb-5 space-y-2" data-testid="birthday-concierge-cards">
              <ConciergeCard
                icon={MapPin}
                title={hasItinerary ? "Your Birthday Route" : "Build Your Itinerary"}
                subtitle={hasItinerary ? "Review stops, times & prep tasks" : (flags.truck_features ? "Map out your birthday deal crawl" : "Map out your birthday game plan")}
                to="/my-itinerary"
                accentColor={userColor}
                delay={0}
                testId="concierge-itinerary"
              />
              <ConciergeCard
                icon={Send}
                title="Know a Deal?"
                subtitle="Submit a deal and earn 30 pts"
                to="/submit-offer"
                accentColor="#10b981"
                delay={0.1}
                testId="concierge-submit"
              />
            </div>

            {/* Worth Another Look — missed deals */}
            <WorthAnotherLook
              daysUntilBirthday={daysUntil}
              onToggleFavorite={toggleFavorite}
              favorites={favorites}
            />
          </motion.div>
        ) : isFriendsMode ? (
          <FriendsHome />
        ) : (
          <motion.div
            key="discover-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="discover-mode"
          >
            {/* POSITION 5 (in-mode): Near Me Now */}
            <NearMeNow city={user?.city || "Sacramento"} />

            {/* POSITION 7: What's New — max 3 items */}
            <WhatsNewPreview />

            {/* POSITION 8: Trending in [City] */}
            <TrendingInCity city={user?.city || "Sacramento"} />

            {/* POSITION 8: Food Trucks Near You — gated at v1.0 by truck_features */}
            {flags.truck_features && <FoodTruckDiscoverSection />}

            {/* POSITION 9: Community Picks — keep upvoting mechanic */}
            <CommunityPicks />

            {/* POSITION 11: Contribute — Gate 5.5 Item 1 */}
            <HomeContributeCards />

            {/* Feature flagged off at v1.0 — hidden, not deleted */}
            {/* <CityProgressRing /> — flag: city_explorer_ring */}
            {/* <DealCalendar /> — flag: deal_calendar */}
            {/* <BirthdaySeasonCountdown /> — flag: birthday_season_countdown */}
            {/* <NewThisWeek /> — flag: new_this_week */}
            {/* <PowerDealsSection /> — flag: power_deals */}

            {/* Concierge Cards — replaced by Primary Actions at position 4 */}
            {/* <div data-testid="discover-concierge-cards">...</div> */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ SHARED SECTIONS (both modes) ═══ */}

      {/* Monthly Challenge Banner — feature flagged off at v1.0 */}
      {/* <MonthlyChallengeBanner /> */}

      {/* Perfect Month Tracker — feature flagged off at v1.0 */}
      {/* <PerfectMonthTracker /> */}

      {/* All Offers Accordion — feature flagged off at v1.0 */}

      {/* All Offers Accordion */}
      {/* All Offers Accordion + Top Picks — feature flagged off at v1.0 */}
      {false && (
      <div className="hidden">
      <AnimatePresence>
        {showWishlistTooltip && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mx-4 mb-2 mt-3 bg-violet-600 text-white rounded-xl px-4 py-3 shadow-lg"
            data-testid="wishlist-tooltip"
          >
            <p className="text-sm font-semibold">Bookmark vs Heart — what's the difference?</p>
            <p className="text-xs text-violet-200 mt-1"><strong className="text-white">Heart</strong> = I like this deal, saving for reference</p>
            <p className="text-xs text-violet-200"><strong className="text-white">Bookmark</strong> = I want this on my birthday plan specifically</p>
            <button onClick={() => setShowWishlistTooltip(false)} className="mt-2 text-[10px] font-bold text-violet-300 hover:text-white">Got it</button>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="px-4 mt-3 flex flex-col gap-3">
        <section data-testid="all-offers-section">
          {selectedCategory.length > 0 && searchParams.get("category") && (
            <div className="bg-teal-50 border border-teal-200 rounded-2xl px-4 py-3 flex items-center justify-between mb-2" data-testid="category-filter-banner">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-500" />
                <span className="text-sm font-semibold text-teal-800">Browsing: {selectedCategory[0]}</span>
                <span className="text-xs text-teal-500">{offers.length} deals</span>
              </div>
              <button
                onClick={() => { setSelectedCategory([]); setSearchParams({}); }}
                className="text-xs font-semibold text-teal-600 hover:text-teal-800 bg-teal-100 px-2.5 py-1 rounded-lg transition"
                data-testid="clear-category-btn"
              >Clear</button>
            </div>
          )}
          <div className="rounded-2xl overflow-hidden shadow-md">
            <button
              onClick={() => setOffersOpen(!offersOpen)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-white transition-all"
              style={{ background: `linear-gradient(to right, ${theme.accordionFrom}, ${theme.accordionTo})` }}
              data-testid="all-offers-accordion-btn"
            >
              <Gift className="w-5 h-5" />
              <span className="text-sm font-bold flex-1 text-left">All Offers</span>
              <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">{offers.length}</span>
              <motion.div animate={{ rotate: offersOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            </button>
            <AnimatePresence>
              {offersOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="bg-white overflow-hidden"
                >
                  <div className="max-h-[65vh] overflow-y-auto p-3 visible-scrollbar">
                    <div className="space-y-3">
                      {offers.map((offer, idx) => (
                        <div key={offer.business_id} data-business-id={offer.business_id}>
                          <SmartOfferCard
                            offer={offer}
                            onClick={() => { logDiscovery(offer.business_id, search ? "search" : selectedCategory.length ? "category" : "browse"); navigate(`/offer/${offer.business_id}`); }}
                            userCity={user?.city}
                            isFavorited={favorites.includes(offer.business_id)}
                            onToggleFavorite={toggleFavorite}
                            isWishlisted={wishlistIds.includes(offer.business_id)}
                            onToggleWishlist={toggleWishlist}
                            onShare={setShareOffer}
                            showHints={idx < 3}
                          />
                        </div>
                      ))}
                      {offers.length === 0 && (
                        <div className="text-center py-10 text-slate-400">
                          <Gift className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          {mode === "discover" && !hasDietaryFilter ? (
                            <>
                              <p className="font-medium" style={{ color: theme.primary }}>Discover Mode Active</p>
                              <p className="text-sm mt-1">Open the Filters sheet and select at least one dietary filter (Halal, Vegan, or Vegetarian) to browse all places.</p>
                            </>
                          ) : (
                            <>
                              <p className="font-medium">No offers found</p>
                              <p className="text-sm mt-1">Try adjusting your filters</p>
                            </>
                          )}
                          {searchSuggestion && (
                            <button
                              onClick={() => setSearch(searchSuggestion)}
                              className="mt-3 text-sm text-sky-500 hover:text-sky-600 font-semibold transition-colors"
                              data-testid="did-you-mean-btn"
                            >
                              Did you mean <span className="underline underline-offset-2">{searchSuggestion}</span>?
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* Top Picks */}
        {topPicks.length > 0 && (
          <section data-testid="top-picks-section">
            <div className="rounded-2xl overflow-hidden shadow-md">
              <button
                onClick={() => setTopPicksOpen(!topPicksOpen)}
                className="w-full flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white transition-all"
                data-testid="top-picks-accordion-btn"
              >
                <Trophy className="w-5 h-5" />
                <span className="text-sm font-bold flex-1 text-left">Top Picks by Category</span>
                <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">{topPicks.length} categories</span>
                <motion.div animate={{ rotate: topPicksOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
              <AnimatePresence>
                {topPicksOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="bg-white overflow-hidden"
                  >
                    <div className="overflow-x-auto border-b border-slate-100 px-1 pt-2 pb-0">
                      <div className="flex gap-1 min-w-max px-2">
                        {topPicks.map((group, i) => (
                          <button
                            key={group.category}
                            onClick={() => setActivePickTab(i)}
                            className={`px-3 py-2 text-xs font-semibold rounded-t-lg whitespace-nowrap transition-colors ${activePickTab === i ? "bg-amber-50 text-amber-700 border-b-2 border-amber-500" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                            data-testid={`top-pick-tab-${group.category.toLowerCase().replace(/[\s/]+/g, '-')}`}
                          >
                            {group.category}
                            <span className={`ml-1.5 text-[10px] ${activePickTab === i ? "text-amber-500" : "text-slate-300"}`}>{group.total_in_category}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="p-3">
                      <AnimatePresence mode="wait">
                        {topPicks[activePickTab] && (
                          <motion.div
                            key={activePickTab}
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.15 }}
                            className="space-y-2"
                          >
                            {(topPicks[activePickTab].top_picks || []).map((offer, idx) => (
                              <div key={offer.business_id} className="flex items-start gap-2">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-3 ${idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-slate-300 text-white" : idx === 2 ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`top-pick-rank-${idx + 1}`}>{idx + 1}</span>
                                <div className="flex-1">
                                  <SmartOfferCard
                                    offer={offer}
                                    onClick={() => { logDiscovery(offer.business_id, "top_picks"); navigate(`/offer/${offer.business_id}`); }}
                                    userCity={user?.city}
                                    isFavorited={favorites.includes(offer.business_id)}
                                    onToggleFavorite={toggleFavorite}
                                    isWishlisted={wishlistIds.includes(offer.business_id)}
                                    onToggleWishlist={toggleWishlist}
                                    onShare={setShareOffer}
                                  />
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => { setSelectedCategory([topPicks[activePickTab].category]); setOffersOpen(true); setSearchParams({ category: topPicks[activePickTab].category }); }}
                              className="w-full py-2.5 text-center text-xs font-semibold text-amber-600 hover:text-amber-700 bg-amber-50 rounded-xl transition-colors"
                              data-testid="top-picks-see-all"
                            >
                              See all {topPicks[activePickTab].total_in_category} {topPicks[activePickTab].category} deals
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>
        )}
      </div>
      </div>
      )}

      {/* Share Sheet */}
      {shareOffer && (
        <ShareSheet
          isOpen={!!shareOffer}
          onClose={() => setShareOffer(null)}
          url={`${window.location.origin}/offer/${shareOffer.business_id}`}
          title={getShareCopy(mode, "offer", { name: shareOffer.name, offerTitle: shareOffer.offer_title }).title}
          description={getShareCopy(mode, "offer", { name: shareOffer.name, offerTitle: shareOffer.offer_title }).description}
          businessId={shareOffer.business_id}
          businessName={shareOffer.name}
          shareContext="offer"
        />
      )}
      {/* Footer links */}
      <div className="flex items-center justify-center gap-4 py-6 mb-16" data-testid="footer-links">
        <a href="/privacy" className="text-[11px] text-slate-400 hover:text-slate-600 min-h-[44px] flex items-center" data-testid="footer-privacy-link">Privacy Policy</a>
        <span className="text-slate-300">|</span>
        <a href="/terms" className="text-[11px] text-slate-400 hover:text-slate-600 min-h-[44px] flex items-center" data-testid="footer-terms-link">Terms of Service</a>
        <span className="text-slate-300">|</span>
        <a href="/about" className="text-[11px] text-slate-400 hover:text-slate-600 min-h-[44px] flex items-center" data-testid="footer-about-link">About</a>
      </div>
      <BottomNav />
      <TooltipOverlay screen="search" targetSelector='[data-testid="search-input"]' message="Search for any restaurant, bakery, or brand to find birthday freebies near you." placement="bottom" />
    </div>
  );
}


/**
 * GreetingEngine — v1.0.0 Gate 1+2
 * Displays the state-driven greeting with countdown that scales by state.
 * Gate 2 Item 2: pulsing at pre_birthday_7, confetti once on birthday_day,
 * post-birthday survey prompt at post_birthday_7.
 */
function GreetingEngine() {
  const { state, greeting, days_until_birthday, display_name, loading, copy_bank_key } = useUserState();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const confettiFired = useRef(false);
  const greetingTrackedRef = useRef(false);

  // v1.0.0 GATE 3 — fire greeting_displayed once per mount after resolved
  useEffect(() => {
    if (greetingTrackedRef.current) return;
    if (loading || !greeting) return;
    greetingTrackedRef.current = true;
    trackGreetingDisplayed(state, copy_bank_key || state);
  }, [state, greeting, loading, copy_bank_key]);

  // Fire confetti once on birthday_day — persisted to localStorage
  useEffect(() => {
    if (state === "birthday_day" && !confettiFired.current) {
      const year = new Date().getFullYear();
      const key = `bdl_confetti_fired_${year}`;
      if (!localStorage.getItem(key)) {
        confettiFired.current = true;
        localStorage.setItem(key, "true");
        try {
          import("canvas-confetti").then(({ default: confetti }) => {
            const colors = ["#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6FC8"];
            confetti({ particleCount: 100, spread: 360, origin: { y: 0.4 }, colors, zIndex: 9999 });
            setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors, zIndex: 9999 }), 400);
            setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors, zIndex: 9999 }), 700);
          });
        } catch {}
      }
    }
  }, [state]);

  if (loading || !greeting) return null;

  const stateStyles = {
    new_user: "from-amber-500 to-orange-500",
    standard: "from-slate-600 to-slate-700",
    pre_birthday_30: "from-orange-500 to-amber-500",
    pre_birthday_7: "from-pink-500 to-rose-500",
    birthday_day: "from-amber-400 via-pink-500 to-purple-500",
    post_birthday_7: "from-indigo-500 to-purple-500",
  };

  const countdownSize = {
    standard: "text-2xl",
    pre_birthday_30: "text-3xl font-black",
    pre_birthday_7: "text-5xl font-black",
    birthday_day: "",
    post_birthday_7: "",
    new_user: "",
  };

  // Pre-birthday-7 gets a pulse animation
  const pulseClass = state === "pre_birthday_7" ? "animate-pulse" : "";

  return (
    <div className="px-4 mt-3 mb-1" data-testid="greeting-engine">
      <div className={`rounded-2xl p-4 bg-gradient-to-r ${stateStyles[state] || stateStyles.standard} ${pulseClass}`}>
        {display_name && (
          <p className="text-white/70 text-xs font-medium mb-0.5" data-testid="greeting-name">
            {display_name}
          </p>
        )}
        <p className="text-white text-sm font-semibold leading-snug" data-testid="greeting-text">
          {greeting}
        </p>

        {/* Birthday day — celebration state */}
        {state === "birthday_day" && (
          <p className="text-white text-xl font-black mt-2 tracking-tight" data-testid="greeting-celebration">
            Happy Birthday!
          </p>
        )}

        {/* Post-birthday — survey prompt + wanted promos CTA */}
        {state === "post_birthday_7" && (
          <div className="mt-3 flex gap-2" data-testid="greeting-post-birthday-ctas">
            <button
              onClick={() => {
                trackPostBirthdaySurveyStarted(
                  days_until_birthday != null ? Math.abs(days_until_birthday) : null
                );
                navigate("/feedback?from=post_birthday_survey");
              }}
              className="flex-1 py-2 px-3 rounded-xl bg-white/20 text-white text-xs font-semibold text-center backdrop-blur-sm"
              data-testid="greeting-take-survey-btn"
            >
              Take the Survey
            </button>
            <button
              onClick={() => navigate("/wishlist")}
              className="flex-1 py-2 px-3 rounded-xl bg-white/20 text-white text-xs font-semibold text-center backdrop-blur-sm"
              data-testid="greeting-wanted-promos-btn"
            >
              Wanted Promos
            </button>
          </div>
        )}

        {/* Countdown number — scales by user state */}
        {days_until_birthday != null && state !== "new_user" && state !== "post_birthday_7" && state !== "birthday_day" && (
          <button
            type="button"
            onClick={() => {
              trackBirthdayCountdownTapped(days_until_birthday, state);
              navigate("/my-birthday");
            }}
            className={`text-white/90 font-bold mt-2 ${countdownSize[state] || "text-2xl"} block text-left`}
            data-testid="greeting-countdown"
          >
            {days_until_birthday} <span className="text-white/60 text-xs font-medium">days to go</span>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * PrimaryActions — v1.0.0 Gate 1 (Fix 3)
 * Position 4 on homepage. Max 3 CTA cards. State-based with fade transition.
 */
function PrimaryActions() {
  const { state } = useUserState();
  const { theme } = useTheme();
  const { flags } = useFeatureFlags();
  const navigate = useNavigate();

  const ctaMatrix = {
    new_user: [
      { label: "Explore Deals", path: "/discover" },
      { label: "Set Up My Birthday", path: "/my-birthday" },
      { label: "Find Food Trucks", path: "/live-map", flag: "truck_features" },
    ],
    standard: [
      { label: "Browse Deals", path: "/discover" },
      { label: "Explore Near Me", path: "/discover" },
      { label: "Submit a Deal", path: "/submit-offer" },
    ],
    pre_birthday_30: [
      { label: "Build My Itinerary", path: "/my-itinerary" },
      { label: "Find Deals Near Me", path: "/discover" },
      { label: "Invite a Friend", path: "/plan-birthday" },
    ],
    pre_birthday_7: [
      { label: "My Saved Deals", path: "/my-deals" },
      { label: "Build My Route", path: "/my-itinerary" },
      { label: "Share My Birthday", path: "/my-birthday" },
    ],
    birthday_day: [
      { label: "Go Get Yours", path: "/my-itinerary" },
      { label: "My Saved Deals", path: "/my-deals" },
      { label: "Share My Birthday", path: "/my-birthday" },
    ],
    post_birthday_7: [
      { label: "Take the Survey", path: "/feedback" },
      { label: "Build Next Year's List", path: "/discover" },
      { label: "Browse Deals", path: "/discover" },
    ],
  };

  const ctas = (ctaMatrix[state] || ctaMatrix.standard).filter(
    (c) => !c.flag || flags[c.flag]
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="px-4 mt-3 mb-2"
        data-testid="primary-actions"
      >
        <div className="flex gap-2">
          {ctas.map((cta, i) => (
            <button
              key={cta.label}
              onClick={() => navigate(cta.path)}
              className="flex-1 py-3 px-2 rounded-xl text-xs font-semibold text-center border transition-colors active:scale-[0.98]"
              style={{
                borderColor: `${theme.primary}25`,
                color: theme.primary,
                backgroundColor: `${theme.primary}08`,
              }}
              data-testid={`primary-action-${i}`}
            >
              {cta.label}
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}


/**
 * WhatsNewPreview — v1.0.0 Gate 1 (Section 1.3)
 * Shows recent What's New items on the homepage between discovery feed and deeper content.
 */
function WhatsNewPreview() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { theme } = useTheme();

  useEffect(() => {
    axios.get(`${API}/whats-new?limit=3`).then((r) => {
      setItems(r.data?.items || r.data || []);
    }).catch(() => {});
  }, []);

  if (!items.length) return null;

  const newCount = items.filter(i => i.is_new).length;

  return (
    <div className="px-4 mb-4" data-testid="whats-new-preview">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-slate-800">What's New</h3>
          {newCount > 0 && (
            <span
              className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: theme.primary }}
              data-testid="whats-new-unread-badge"
            >
              {newCount}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate("/whats-new")}
          className="text-xs font-semibold"
          style={{ color: theme.primary }}
          data-testid="whats-new-see-all"
        >
          See all
        </button>
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 3).map((item, i) => {
          const badgeColors = {
            "New": "bg-emerald-100 text-emerald-700",
            "Updated": "bg-sky-100 text-sky-700",
            "Enhanced": "bg-amber-100 text-amber-700",
            "Expanded": "bg-purple-100 text-purple-700",
            "Fixed": "bg-slate-100 text-slate-700",
            "Coming Soon": "bg-gray-100 text-gray-500",
          };
          const badge = item.badge || "New";
          const publishedAt = item.published_at || item.created_at || item.date;
          const daysSincePublished = publishedAt
            ? Math.max(0, Math.floor((Date.now() - new Date(publishedAt).getTime()) / 86400000))
            : null;
          return (
            <button
              type="button"
              key={item.id || i}
              onClick={() => {
                trackWhatsNewItemViewed(item.id || item.slug || `item_${i}`, badge, daysSincePublished);
                navigate("/whats-new");
              }}
              className="w-full flex items-center gap-2 py-2 px-3 bg-white rounded-xl border border-slate-100 text-left"
              data-testid={`whats-new-item-${i}`}
            >
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${badgeColors[badge] || badgeColors["New"]}`}>
                {badge}
              </span>
              <span className="text-xs text-slate-700 flex-1 truncate">{item.headline || item.title || item.description}</span>
              {item.is_new && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/**
 * NewUserEmptyState — v1.0.0 Gate 1 (Section 1.5)
 * Shows for new_user state when there are no deals loaded yet.
 */
function NewUserEmptyState() {
  const { state } = useUserState();
  const { theme } = useTheme();
  const navigate = useNavigate();

  if (state !== "new_user") return null;

  return (
    <div className="flex items-center justify-center min-h-[40vh]" data-testid="new-user-empty-state">
      <div className="text-center px-8">
        <p className="text-base font-semibold text-slate-700 leading-relaxed">
          Your birthday adventure starts here.<br />
          Let's find your first deal.
        </p>
        <button
          onClick={() => navigate("/discover")}
          className="mt-6 px-6 py-3 rounded-full text-white text-sm font-semibold"
          style={{ backgroundColor: theme.buttonBg }}
          data-testid="new-user-explore-deals-btn"
        >
          Explore Deals
        </button>
      </div>
    </div>
  );
}
